import React, { useState, useEffect, useContext } from "react";
import { axiosInstance, partiuClient } from "../../axios";
import { GradientButton } from "../atoms/GradientButton";
import {
  Heading,
  Text,
  VStack,
  Input,
  Select,
  HStack,
  CircularProgress,
  Button,
  useToast,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { CompanyContext } from "../../contexts/CompanyContext";
import CurrencyInput from "react-currency-input-field";

export const GenerateSale = () => {
  // Estados usando useState padrão do React
  const [virtualCard, setVirtualCard] = useState("");
  const [discounts, setDiscounts] = useState([]);
  const [selectedDiscount, setSelectedDiscount] = useState("");
  const [total, setTotal] = useState("");
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [agreements, setAgreements] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState("");

  const { company, store } = useContext(CompanyContext);
  const toast = useToast();

  // useEffect com dependências corretas
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Buscar descontos
        const discountsResponse = await axiosInstance.get(`/discounts/?company=${company.id}`);
        const discountsData = discountsResponse.data.results;
        setDiscounts(discountsData);
        
        // Selecionar primeiro desconto se existir
        if (discountsData.length > 0) {
          setSelectedDiscount(discountsData[0].id);
        }

        // Buscar convênios
        const agreementsResponse = await axiosInstance.get("/pdv/agreements-store/", {
          headers: {
            "x-store-id": store.id,
          },
        });
        const agreementsData = agreementsResponse.data.agreements;
        setAgreements(agreementsData);
        
        // Selecionar primeiro convênio se existir
        if (agreementsData.length > 0) {
          setSelectedAgreement(agreementsData[0].id);
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        toast({
          title: "Erro ao carregar dados",
          description: "Não foi possível carregar os dados necessários.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    if (company.id && store.id) {
      fetchData();
    }
  }, [company.id, store.id, toast]);

  const handleValidate = async () => {
    if (virtualCard.length !== 11) {
      toast({
        title: "Código Inválido!",
        description: "O código deve conter 11 dígitos. Por favor, verifique o código novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!selectedAgreement) {
      toast({
        title: "Convênio não selecionado!",
        description: "Por favor, selecione um convênio antes de continuar.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      console.log("Validando CPF:", virtualCard);
      const response = await axiosInstance.get(`/pdv/validate-cpf/`, {
        params: {
          cpf: virtualCard,
        },
        headers: {
          "x-agreement-id": selectedAgreement,
        },
      });

      console.log(response.data);
      setUserData(response.data);
      
      toast({
        title: "CPF Validado!",
        description: "Cliente encontrado com sucesso.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Código Inválido!",
        description: error?.response?.data?.error ||
          "O código ou CPF não está presente em nossos banco de dados. Por favor, verifique o código novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setVirtualCard("");
      setUserData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const generate = async () => {
    if (!total || parseFloat(total.replace(",", ".")) <= 0) {
      toast({
        title: "Valor inválido!",
        description: "Por favor, insira um valor válido para a venda.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!selectedDiscount) {
      toast({
        title: "Desconto não selecionado!",
        description: "Por favor, selecione um desconto antes de gerar a venda.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      await partiuClient.salesApp.sales.createStoreSale(store.id, {
        virtual_card: virtualCard,
        discount_id: selectedDiscount,
        pdv_given_total: total.replace(",", "."),
      });

      toast({
        title: "Sucesso!",
        description: "Venda foi adicionada ao banco de dados com sucesso.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Limpar formulário
      setVirtualCard("");
      setUserData(null);
      setTotal("");
      setSelectedDiscount(discounts.length > 0 ? discounts[0].id : "");

      // Recarregar página após delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast({
        title: "Erro ao gerar venda!",
        description: "O código não está presente em nossos banco de dados. Por favor, verifique o código novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCancel = () => {
    setVirtualCard("");
    setUserData(null);
    setTotal("");
    setSelectedDiscount(discounts.length > 0 ? discounts[0].id : "");
    
    toast({
      title: "Operação cancelada",
      description: "Todos os campos foram limpos.",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  if (company.status !== "approved") {
    const status = {
      "pending-approval": "Pendente de aprovação da Partiu",
      "pending-terms": "Entre em contato com o suporte",
      approved: "Entre em contato com o suporte",
      "pending-training": "Pendente de treinamento",
    };
    
    return (
      <VStack width="100%" margin="auto" alignItems="flex-start">
        <FontAwesomeIcon
          icon={faLock}
          fontSize="40px"
          style={{ alignSelf: "center", paddingBottom: "20px" }}
        />
        <Heading fontSize="16px">Em processo de aprovação.</Heading>
        <Text paddingBottom="10px">
          <span style={{ fontWeight: "bold" }}>Status: </span>{" "}
          {status[company.status]}
        </Text>
        <Text>
          Quando sua empresa for aprovada, será possível adicionar vendas no
          sistema.
        </Text>
        <Text>
          Está com alguma dúvida? Envie-nos um email:{" "}
          <a href="mailto:suporte@partiu.com.br">suporte@partiu.com.br</a>
        </Text>
      </VStack>
    );
  }

  return (
    <VStack width="100%" spacing={4}>
      <VStack width="100%">
        <Input
          placeholder="Insira o número do CPF"
          label="CPF"
          errorMessage="Código inválido ou expirado."
          onChange={(e) => setVirtualCard(e.target.value)}
          disabled={isLoading}
          value={virtualCard}
        />

        <Select
          value={selectedAgreement}
          onChange={(e) => setSelectedAgreement(e.target.value)}
        >
          <option value="">Selecione um convênio</option>
          {agreements.map((agreement) => (
            <option key={agreement.id} value={agreement.id}>
              {agreement.name}
            </option>
          ))}
        </Select>
        
        <Button
          colorScheme="green"
          variant="outline"
          disabled={isLoading}
          width="100%"
          onClick={handleValidate}
        >
          Validar
        </Button>
        
        {isLoading && <CircularProgress size="25px" isIndeterminate />}
      </VStack>

      {userData && (
        <VStack spacing={4} width="100%">
          <Select
            value={selectedDiscount}
            onChange={(e) => setSelectedDiscount(e.target.value)}
          >
            <option value="">Selecione um desconto</option>
            {discounts.map((discount) => (
              <option key={discount.id} value={discount.id}>
                {discount.name}
              </option>
            ))}
          </Select>
          
          <CurrencyInput
            placeholder="Valor da Venda"
            label="Valor da Venda"
            decimalsLimit={2}
            decimalSeparator=","
            groupSeparator="."
            defaultValue={0}
            prefix="R$ "
            style={{
              width: "100%",
              border: "1px solid rgb(226, 232, 240)",
              padding: "7px 15px",
              borderRadius: "5px",
            }}
            allowNegativeValue={false}
            onValueChange={(value) => setTotal(value)}
            value={total}
          />
          
          <HStack width="100%">
            <GradientButton onClick={generate} text="Gerar Venda" />
            <Button
              colorScheme="gray"
              variant="solid"
              width="100%"
              onClick={handleCancel}
            >
              Cancelar
            </Button>
          </HStack>
        </VStack>
      )}
    </VStack>
  );
};
